/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Music, Heart, Building2, Camera, Mic2, Sparkles, Users, Lightbulb,
  Phone, ArrowRight, MapPin, ShieldCheck, Clock, Star, Quote,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { useSeo } from "@/lib/seo";
import { REVIEWS } from "@/data/reviews";
import { reviewsApi } from "@/lib/api";
import NextEventBanner from "@/components/NextEventBanner";

const HERO_POSTER = "/videos/hero-poster.webp";
const HERO_POSTER_FALLBACK = "/videos/hero-poster.jpg";
const HERO_VIDEO = "/videos/hero.mp4";

const SERVICES = [
  { icon: Heart, title: "DJ Mariage", desc: "Cérémonie, vin d'honneur, ouverture de bal, soirée dansante.", to: "/mariage", span: "md:col-span-2 md:row-span-2" },
  { icon: Users, title: "DJ Anniversaire", desc: "Fêtes familiales, départs en retraite, soirées privées.", to: "/anniversaire", span: "md:col-span-2" },
  { icon: Building2, title: "Entreprises", desc: "Galas, séminaires, inaugurations, afterworks.", to: "/entreprises", span: "md:col-span-2" },
  { icon: Camera, title: "Photobooth", desc: "Forfaits dès 179 € — photos numériques illimitées.", to: "/photobooth", span: "md:col-span-2" },
  { icon: Sparkles, title: "Collectivités & Mairies", desc: "Fête de la Musique, 14 juillet, événements publics.", to: "/collectivites", span: "md:col-span-2" },
  { icon: Mic2, title: "Blind Test & Karaoké", desc: "Animations interactives pour bars, entreprises, soirées privées.", to: "/blind-test-karaoke", span: "md:col-span-2" },
  { icon: Music, title: "Chanteur Live & Musiciens", desc: "Saxophoniste, chanteur, guitariste pour une ambiance unique.", to: "/prestations", span: "md:col-span-2" },
  { icon: Lightbulb, title: "Mise en lumière", desc: "Éclairage architectural, lyres, effets scéniques.", to: "/prestations", span: "md:col-span-2" },
];

const FAQS = [
  { q: "Intervenez-vous pour les mariages ?", a: "Oui, My Animation propose des prestations DJ mariage complètes : cérémonie, vin d'honneur, repas, ouverture de bal, soirée dansante, sonorisation et mise en lumière." },
  { q: "Fournissez-vous le matériel son et lumière ?", a: "Oui, la prestation peut inclure la sonorisation, l'éclairage, les micros pour discours ou cérémonie et la mise en lumière adaptée au lieu." },
  { q: "Proposez-vous un photobooth ?", a: "Oui, My Animation propose plusieurs forfaits photobooth avec photos numériques illimitées, impressions selon forfait et accès en ligne aux photos avec identifiants." },
  { q: "Dans quelles zones intervenez-vous ?", a: "My Animation intervient principalement dans les Hauts-de-France, le Nord, le Pas-de-Calais et la Belgique." },
  { q: "Comment demander un devis ?", a: "Il suffit de remplir le formulaire de demande de devis en indiquant le type d'événement, la date, le lieu, le nombre d'invités et les prestations souhaitées." },
];

export default function Home() {
  const { t } = useTranslation();
  useSeo({
    title: "DJ événementiel Hauts-de-France | My Animation",
    description:
      "My Animation anime vos mariages, anniversaires, soirées privées, entreprises et événements publics dans les Hauts-de-France, le Nord, le Pas-de-Calais et la Belgique. Demandez votre devis personnalisé.",
    path: "/",
    keywords:
      "DJ événementiel Hauts-de-France, DJ Lille, DJ Tourcoing, DJ Roubaix, DJ mariage Nord, DJ entreprise Lille, photobooth Nord, My Animation",
  });

  // Live reviews + aggregate rating for the testimonials section
  const [liveReviews, setLiveReviews] = useState([]);
  const [liveStats, setLiveStats] = useState(null);

  useEffect(() => {
    Promise.all([reviewsApi.publicList(3), reviewsApi.publicStats()])
      .then(([items, stats]) => {
        setLiveReviews(Array.isArray(items) ? items : []);
        setLiveStats(stats);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="bg-[#050505] text-white">
      <NextEventBanner />
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden grain">
        {/* Background vidéo (desktop + mobile sources) avec poster LCP optimisé.
            Fallback <img> si autoplay bloqué (iOS Low Power Mode, Save-Data, etc.) */}
        <div
          className="absolute inset-0 overflow-hidden"
          aria-hidden="true"
        >
          <video
            data-testid="hero-video"
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={HERO_POSTER}
            disablePictureInPicture
            disableRemotePlayback
          >
            <source src={HERO_VIDEO} type="video/mp4" />
            {/* Fallback image si <video> non supporté */}
            <img src={HERO_POSTER_FALLBACK} alt="" className="w-full h-full object-cover" />
          </video>
          {/* Overlays sombres conservés à l'identique */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </div>
        <div className="spotlight bg-[#DCB457]/30 w-[600px] h-[600px] -top-40 -left-40" />
        <div className="spotlight glow-blush w-[520px] h-[520px] top-20 right-10" />
        <div className="spotlight glow-royal w-[400px] h-[400px] bottom-0 right-40" />

        <div className="relative z-10 max-w-[1400px] mx-auto w-full px-6 md:px-10 lg:px-14 py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6 reveal">
              <span className="gold-line" />
              <span className="eyebrow">{t("home.hero_eyebrow", "CRÉATEUR D'ÉVÉNEMENTS SUR MESURE")}</span>
            </div>
            <h1
              data-testid="hero-title"
              className="font-serif-luxe text-[3rem] sm:text-[4.5rem] md:text-[6rem] lg:text-[7rem] leading-[0.95] tracking-tight reveal"
            >
              {t("home.hero_title_main", "L'événement")}<br />
              <span className="blush-text italic">{t("home.hero_title_italic_alt", "prend vie.")}</span>
            </h1>
            <p className="mt-8 text-white/75 text-base md:text-lg max-w-xl leading-relaxed reveal">
              {t("home.hero_subtitle_long", "DJ, animation, photobooth, artistes live et solutions événementielles sur mesure pour vos mariages, anniversaires, entreprises, collectivités et événements privés dans les Hauts-de-France et en Belgique.")}
            </p>

            <div className="mt-10 flex flex-wrap gap-3 reveal">
              <Link to="/contact" data-testid="hero-devis-btn" className="btn-gold">
                {t("common.get_quote")} <ArrowRight size={16} strokeWidth={1.4} />
              </Link>
              <a href="tel:0769224956" data-testid="hero-call-btn" className="btn-ghost-gold">
                <Phone size={15} strokeWidth={1.4} /> {t("home.hero_call_btn", "Appeler maintenant")}
              </a>
              <Link to="/galerie" data-testid="hero-realisations-btn" className="btn-ghost-gold">
                {t("home.hero_gallery_btn", "Voir les réalisations")}
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl reveal">
              {[
                { icon: MapPin, v: "Hauts-de-France", l: "Nord · Pas-de-Calais · Belgique" },
                { icon: ShieldCheck, v: "Pro & assuré", l: "SIRET 53238715600039 · RC Pro RCP250814180127" },
                { icon: Clock, v: "Réponse < 24h", l: "Devis personnalisé" },
              ].map((s) => (
                <div key={s.l} className="border-l border-[#DCB457]/30 pl-4">
                  <s.icon size={20} strokeWidth={1.2} className="text-[#DCB457] mb-2" />
                  <div className="font-serif-luxe text-lg md:text-xl">{s.v}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[11px] uppercase tracking-[0.3em] text-white/40">défilez ↓</div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#DCB457]/15 py-6 overflow-hidden bg-black">
        <div className="marquee-track flex gap-12 whitespace-nowrap">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-6">
              {["DJ Mariage", "DJ Anniversaire", "Entreprises", "Collectivités & Mairies", "Photobooth", "Blind Test & Karaoké", "Mise en Lumière", "Chanteur Live"].map((t) => (
                <React.Fragment key={t + i}>
                  <span className="font-serif-luxe italic text-2xl md:text-3xl text-white/75">{t}</span>
                  <span className="text-[#DCB457] text-xl">✦</span>
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow">Notre signature</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Créateur <br />
              <span className="italic gold-text">d'ambiances.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 reveal">
            <p className="text-white/70 text-base md:text-lg leading-relaxed">
              My Animation conçoit des prestations DJ et événementielles
              haut de gamme, claires et rassurantes. De Lille à Tourcoing,
              Roubaix, Armentières, Dunkerque, Lens ou Arras — et jusqu'en
              Belgique — nous accompagnons particuliers, entreprises,
              mairies, associations et résidences seniors avec la même
              exigence : votre événement, à votre image.
            </p>
            <div className="grid grid-cols-2 gap-6 mt-10">
              {[
                ["Écoute", "Entretien préparatoire, repérage et coordination."],
                ["Matériel pro", "Sonorisation, éclairage, micros, effets scéniques."],
                ["Discrétion", "Une présence scénique sobre et professionnelle."],
                ["Engagement", "Devis transparent, contrat clair, ponctualité."],
              ].map(([t, d]) => (
                <div key={t} className="border-t border-[#DCB457]/20 pt-4">
                  <h3 className="font-serif-luxe text-xl text-white">{t}</h3>
                  <p className="text-sm text-white/55 mt-1.5 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRESTATIONS BENTO */}
      <section className="relative py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto overflow-hidden" data-testid="services-section">
        <div className="spotlight glow-plum w-[450px] h-[450px] -top-20 -left-20 -z-0" />
        <div className="spotlight glow-coral w-[380px] h-[380px] bottom-10 -right-20 -z-0" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="reveal">
            <span className="eyebrow">Prestations</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
              Une gamme <span className="italic gold-text">complète,</span><br />un seul interlocuteur.
            </h2>
          </div>
          <Link to="/prestations" data-testid="services-cta-all" className="btn-ghost-gold self-start md:self-auto">
            Découvrir les prestations <ArrowRight size={14} strokeWidth={1.4} />
          </Link>
        </div>

        <div className="relative z-10 grid grid-cols-2 md:grid-cols-6 gap-4 md:gap-6 auto-rows-[180px] md:auto-rows-[220px]">
          {SERVICES.map(({ icon: Icon, title, desc, to, span }) => (
            <Link
              to={to}
              key={title}
              data-testid={`service-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`card-luxe p-6 md:p-7 flex flex-col justify-between col-span-2 ${span} reveal group`}
            >
              <Icon size={28} strokeWidth={1} className="text-[#DCB457]" />
              <div>
                <h3 className="font-serif-luxe text-2xl md:text-3xl">{title}</h3>
                <p className="text-sm text-white/55 mt-2 leading-relaxed">{desc}</p>
                <span className="inline-flex items-center gap-2 mt-4 text-[11px] uppercase tracking-[0.2em] text-[#DCB457] group-hover:text-[#F0D888] transition">
                  Découvrir <ArrowRight size={12} strokeWidth={1.4} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FOCUS MARIAGE */}
      <FocusSection
        eyebrow="Mariage"
        title={<>Le grand jour, <span className="italic gold-text">orchestré.</span></>}
        text="Une scénographie sonore et lumineuse pour votre mariage : cérémonie, vin d'honneur, repas, ouverture de bal et soirée dansante. Tout est pensé pour vous ressembler, dans une ambiance élégante et festive."
        bullets={["Playlist personnalisée", "Sonorisation cérémonie & vin d'honneur", "Mise en lumière sur-mesure", "Maître de cérémonie possible"]}
        cta="Découvrir le mariage"
        ctaTo="/mariage"
        img="/images/focus-mariage.webp"
        reversed={false}
      />

      {/* FOCUS ENTREPRISE */}
      <FocusSection
        eyebrow="Entreprises & Collectivités"
        title={<>Galas, séminaires, <span className="italic gold-text">événements publics.</span></>}
        text="My Animation accompagne entreprises, mairies, collectivités et associations pour vos galas, inaugurations, fêtes municipales, Fête de la Musique, 14 juillet et événements professionnels — de Lille à Dunkerque en passant par Arras et Valenciennes."
        bullets={["Devis & contrat sous 24h", "Régie son, lumière, vidéo", "Sonorisation discours & cérémonie", "Intervention dans tout le Nord & Pas-de-Calais"]}
        cta="Solutions entreprises"
        ctaTo="/entreprises"
        video="/videos/focus-entreprise.mp4"
        poster="/videos/focus-entreprise-poster.webp"
        reversed={true}
      />

      {/* FOCUS PHOTOBOOTH */}
      <FocusSection
        eyebrow="Photobooth"
        title={<>Photobooth <span className="italic gold-text">à partir de 179 €.</span></>}
        text="Six forfaits photobooth pour s'adapter à votre événement : photos numériques illimitées sur tous les forfaits, impressions selon votre besoin, thèmes personnalisables et accès en ligne aux photos avec identifiants."
        bullets={["Photos numériques illimitées incluses", "De 100 à 400 impressions selon forfait", "Thèmes personnalisables", "Galerie en ligne sécurisée"]}
        cta="Réserver mon photobooth"
        ctaTo="/photobooth"
        img="https://images.pexels.com/photos/9342943/pexels-photo-9342943.jpeg"
        reversed={false}
      />

      {/* TÉMOIGNAGES (vrais avis Google) */}
      <section className="relative py-24 md:py-32 overflow-hidden grain" data-testid="testimonials-section">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        </div>
        <div className="spotlight bg-[#DCB457]/12 w-[500px] h-[500px] top-10 right-0" />
        <div className="spotlight glow-blush w-[420px] h-[420px] bottom-10 left-10" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="text-center mb-16 reveal">
            <span className="eyebrow">Retours clients</span>
            <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
              Des moments qui <span className="italic gold-text">résonnent.</span>
            </h2>
            <div className="mt-5 flex items-center justify-center gap-2">
              {[...Array(5)].map((_, i) => {
                const avg = liveStats?.average || 4.9;
                const filled = i < Math.round(avg);
                return (
                  <Star key={i} size={18} className={filled ? "fill-[#DCB457] text-[#DCB457]" : "text-[#DCB457]/30"} />
                );
              })}
              <span className="ml-2 text-sm text-white/70">
                {liveStats?.average ? liveStats.average.toFixed(1).replace(".", ",") : "4,9"} / 5
                {liveStats?.count ? ` sur ${liveStats.count} avis clients` : " sur 8 avis clients"}
              </span>
            </div>
            <p className="mt-3 text-xs md:text-sm text-white/55 max-w-xl mx-auto">
              Avis publiés sur Google et Mariages.net, complétés par des retours clients directs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(liveReviews.length > 0
              ? liveReviews.slice(0, 3).map((r) => ({
                  key: r.id,
                  name: r.author_name,
                  role: r.event_type + (r.city ? ` · ${r.city}` : ""),
                  when: r.event_date ? new Date(r.event_date).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "",
                  stars: r.rating,
                  text: r.content,
                  testid: `home-review-${r.id}`,
                  href: `/avis#review-${r.id}`,
                }))
              : REVIEWS.map((r, i) => ({
                  key: r.name,
                  name: r.name,
                  role: r.role,
                  when: r.when,
                  stars: r.stars,
                  text: r.text,
                  testid: `review-card-${i}`,
                  href: "/avis",
                }))
            ).map((r) => (
              <Link
                key={r.key}
                to={r.href}
                data-testid={r.testid}
                className="card-luxe p-7 md:p-8 reveal flex flex-col hover:border-[#DCB457]/45 transition group"
              >
                <Quote size={26} strokeWidth={1} className="text-[#DCB457] mb-4" />
                <p className="text-white/85 leading-relaxed text-[15px] flex-1 italic line-clamp-6">
                  «&nbsp;{r.text}&nbsp;»
                </p>
                <div className="mt-6 flex items-center justify-between border-t border-[#DCB457]/15 pt-5">
                  <div>
                    <div className="text-sm font-medium text-white group-hover:text-[#DCB457] transition">{r.name}</div>
                    {(r.role || r.when) && (
                      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mt-1">
                        {r.role}{r.role && r.when ? " · " : ""}{r.when}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-0.5">
                    {[...Array(r.stars)].map((_, j) => (
                      <Star key={j} size={12} className="fill-[#DCB457] text-[#DCB457]" />
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* CTA voir tous les avis */}
          <div className="text-center mt-12 reveal">
            <Link to="/avis" data-testid="home-reviews-all" className="btn-ghost-gold inline-flex items-center gap-2">
              Voir tous les avis <ArrowRight size={13} strokeWidth={1.4} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto" data-testid="faq-section">
        <div className="text-center mb-14 reveal">
          <span className="eyebrow">Questions fréquentes</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-3 leading-[1.05]">
            Tout ce que vous <span className="italic gold-text">devez savoir.</span>
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="card-luxe !rounded-none px-6 md:px-8">
              <AccordionTrigger className="font-serif-luxe text-xl md:text-2xl text-left hover:no-underline data-[state=open]:text-[#DCB457] py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/65 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="text-center mt-10">
          <Link to="/faq" className="btn-ghost-gold">Toutes les questions <ArrowRight size={14} strokeWidth={1.4} /></Link>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/9665186/pexels-photo-9665186.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="DJ My Animation en soirée événementielle, Hauts-de-France" loading="lazy" decoding="async" width="1200" height="800" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/60" />
        </div>
        <div className="spotlight glow-blush w-[500px] h-[500px] top-10 right-10" />
        <div className="spotlight glow-royal w-[400px] h-[400px] bottom-0 left-20" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="max-w-3xl reveal">
            <span className="eyebrow">Démarrons ensemble</span>
            <h2 className="font-serif-luxe text-5xl md:text-6xl lg:text-7xl mt-4 leading-[0.98]">
              Parlons de votre<br />
              <span className="italic blush-text">événement.</span>
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-xl">
              Devis gratuit, personnalisé et sans engagement. Nous vous répondons rapidement avec une proposition adaptée à votre projet.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="cta-final-devis-btn" className="btn-gold">Demander un devis <ArrowRight size={16} strokeWidth={1.4} /></Link>
              <a href="tel:0769224956" data-testid="cta-final-call-btn" className="btn-ghost-gold"><Phone size={15} strokeWidth={1.4} /> 07 69 22 49 56</a>
              <a href="mailto:my-animation@outlook.fr" className="btn-ghost-gold">Nous écrire</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FocusSection({ eyebrow, title, text, bullets, cta, ctaTo, img, video, poster, reversed }) {
  return (
    <section className="py-24 md:py-32 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${reversed ? "lg:[direction:rtl]" : ""}`}>
        <div className={`lg:col-span-7 reveal ${reversed ? "lg:[direction:ltr]" : ""}`}>
          <div className="relative overflow-hidden">
            {video ? (
              <video
                className="w-full h-[480px] md:h-[600px] object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                poster={poster}
                disablePictureInPicture
                disableRemotePlayback
                aria-label={typeof title === "string" ? title : "focus"}
              >
                <source src={video} type="video/mp4" />
                {poster && <img src={poster} alt={typeof title === "string" ? title : "focus"} className="w-full h-full object-cover" />}
              </video>
            ) : (
              <img src={img} alt={typeof title === "string" ? title : "focus"} loading="lazy" decoding="async" width="1200" height="800" className="w-full h-[480px] md:h-[600px] object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
          </div>
        </div>
        <div className={`lg:col-span-5 reveal ${reversed ? "lg:[direction:ltr]" : ""}`}>
          <span className="eyebrow">{eyebrow}</span>
          <h2 className="font-serif-luxe text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.02]">{title}</h2>
          <p className="text-white/70 text-base md:text-lg leading-relaxed mt-6">{text}</p>
          <ul className="mt-8 space-y-3">
            {bullets.map((b) => (
              <li key={b} className="flex items-center gap-3 text-sm text-white/80">
                <span className="w-1.5 h-1.5 bg-[#DCB457]" />{b}
              </li>
            ))}
          </ul>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to={ctaTo} className="btn-gold">{cta} <ArrowRight size={14} strokeWidth={1.4} /></Link>
            <Link to="/contact" className="btn-ghost-gold">Demander un devis</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
